// One lead: /growth/leads/:id
//
// Each field saves itself, as on a tenant's Settings tab. Do not contact is set
// by the unsubscribe link in the emails, never by hand; on such a lead the status
// and the Delete button are gone, because the server refuses both (409 do_not_contact).

import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import { BUCKETS, LEAD_STATUSES, LEAD_TYPES, type ActivityStatus, type Bucket, type Lead, type LeadActivity, type LeadStatus, type LeadWrite, type VerifyDecisionStatus, type VerifyState } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  ConfirmDialog,
  EditableSelectField,
  EditableTextField,
  EmptyState,
  ErrorState,
  FormField,
  InlineAlert,
  KeyValueList,
  LoadingState,
  RelativeTime,
  Textarea,
  TrashIcon,
  useToast,
} from '@/components/ui'
import { formatDate } from '@/lib/format'
import { ACTIVITY_LABELS, activityDay, BUCKET_LABELS, LEAD_STATUS_LABELS, LEAD_TYPE_LABELS, platformPrice, VERIFY_LABELS, VERIFY_RUN_WORDS } from '@/lib/growth'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'
import { useActivityRun } from '@/lib/useFinder'
import { ActivityDetails, ActivityRunBanner } from './activityShared'
import { EmailActivityText } from './LeadEmailActivity'
import { LeadEmailButton } from './LeadEmail'
import { LeadConversation } from './LeadConversation'
import { LeadPreviewAction, LeadPreviewCard } from './LeadPreviewCard'
import { BucketBadge, PriceVsBadge, SiteSignalList, VsBadge } from './platformShared'
import { LeadStatusBadge, SafeExternalLink } from './shared'
import { DecisionNote, VerifyDetails } from './verifyShared'

const STATUS_OPTIONS = LEAD_STATUSES.filter((s) => s !== 'do_not_contact').map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] }))
const TYPE_OPTIONS = LEAD_TYPES.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] }))

function statusLabel(v: unknown): string {
  return LEAD_STATUS_LABELS[v as LeadStatus] ?? String(v)
}

const PREVIEW_ACTIONS: Record<string, string> = {
  extended: 'Preview extended',
  expired: 'Preview ended, and its site was deleted',
  converted: 'Preview converted to a real league',
  deleted: 'Preview deleted',
  do_not_contact: 'Preview deleted: do not contact',
  // Before November 3, when a preview was a link that could be turned off.
  turned_off: 'Preview turned off',
  brought_back: 'Preview brought back',
  claimed: 'Preview claimed',
}

/** The line a timeline entry reads. A kind this page doesn't know (a later stage's) still shows. */
function ActivityText({ item }: { item: LeadActivity }) {
  const inquiryId = Number(item.detail.inquiry_id)
  const inquiry = Number.isInteger(inquiryId) && inquiryId > 0 ? (
    <Link to={`/growth/inquiries/${inquiryId}`} className="underline underline-offset-2 hover:text-accent">
      inquiry #{inquiryId}
    </Link>
  ) : null
  switch (item.kind) {
    case 'note':
      return <p className="whitespace-pre-wrap break-words text-[13.5px] text-ink">{item.body}</p>
    case 'status_change':
      return (
        <p className="text-[13.5px] text-ink">
          {statusLabel(item.detail.from)} → {statusLabel(item.detail.to)}
        </p>
      )
    case 'imported':
      return <p className="text-[13.5px] text-ink">Imported from {String(item.detail.source ?? 'a CSV')}</p>
    case 'inquiry':
      return <p className="text-[13.5px] text-ink">Linked to {inquiry ?? 'an inquiry'}</p>
    case 'created':
      return <p className="text-[13.5px] text-ink">Added{inquiry ? <> from {inquiry}</> : null}</p>
    case 'email_sent':
    case 'email_dry_run':
    case 'email_skipped':
    case 'email_clicked':
    case 'email_bounced':
    case 'email_replied':
    case 'email_unsubscribed':
      return <EmailActivityText item={item} />
    case 'activity_checked':
      return (
        <p className="text-[13.5px] text-ink">
          Activity checked: {ACTIVITY_LABELS[item.detail.status as ActivityStatus] ?? String(item.detail.status)}
          {activityDay(item.detail.last_activity_on as string | null) ? `, last activity ${activityDay(item.detail.last_activity_on as string | null)}` : ''}
        </p>
      )
    case 'verified':
      // A person's call (POST /leads/:id/verification), with their note if they left one.
      if (item.detail.by_hand) {
        return (
          <p className="whitespace-pre-wrap break-words text-[13.5px] text-ink">
            {item.detail.status === 'needs_review' ? 'Put back for review' : `Marked ${(VERIFY_LABELS[item.detail.status as VerifyState] ?? String(item.detail.status)).toLowerCase()} by hand`}
            {item.body ? `: ${item.body}` : ''}
          </p>
        )
      }
      return (
        <p className="text-[13.5px] text-ink">
          Verified: {VERIFY_LABELS[item.detail.status as VerifyState] ?? String(item.detail.status)}
          {item.detail.platform_was !== item.detail.platform ? `, ${String(item.detail.platform_was)} → ${String(item.detail.platform)}` : ''}
        </p>
      )
    case 'preview_created':
      return <p className="text-[13.5px] text-ink">Preview built: {String(item.detail.teams)} teams, {String(item.detail.games)} games</p>
    case 'preview_viewed':
      return <p className="text-[13.5px] text-ink">Signed in to their preview ({String(item.detail.view_count)} {item.detail.view_count === 1 ? 'time' : 'times'} so far)</p>
    case 'preview_removed':
      return <p className="text-[13.5px] font-semibold text-ink">Someone at the league removed the preview</p>
    case 'preview_changed':
      return <p className="text-[13.5px] text-ink">{PREVIEW_ACTIONS[String(item.detail.action)] ?? 'Preview changed'}</p>
    default:
      return <p className="text-[13.5px] text-ink">{item.kind.replace(/_/g, ' ')}</p>
  }
}

function Timeline({ leadId }: { leadId: number }) {
  const { data, error, status, refetch } = useQuery(['growth', 'activity', leadId], (signal) => api.growth.leads.activity(leadId, signal))
  const [note, setNote] = useState('')
  const [pending, setPending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!note.trim()) return
    setPending(true)
    setProblem(null)
    try {
      await api.growth.leads.addNote(leadId, note.trim())
      setNote('')
      invalidateQueries('growth')
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Card>
      <CardHeader title="Timeline" />
      <form onSubmit={(e) => void submit(e)} className="grid gap-2">
        <FormField label="Add a note">
          {({ id }) => <Textarea id={id} value={note} maxLength={5000} onChange={(e) => setNote(e.target.value)} disabled={pending} />}
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={!note.trim()} loading={pending}>
            Add note
          </Button>
        </div>
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      </form>
      {status === 'loading' ? (
        <LoadingState label="Loading timeline…" />
      ) : status === 'error' && !data ? (
        <ErrorState error={error} title="Couldn't load the timeline" onRetry={refetch} />
      ) : (
        <ol aria-label="Timeline" className="mt-4 grid gap-3 border-t border-line pt-4">
          {(data ?? []).map((item) => (
            <li key={item.id} className="grid gap-0.5">
              <ActivityText item={item} />
              <p className="text-xs text-muted">
                <RelativeTime value={item.created_at} className="text-muted" />
                {item.actor ? ` · ${item.actor.name || item.actor.email}` : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}

/** What they use today, how that compares, and why. */
function PlatformCard({ lead, save }: { lead: Lead; save: (patch: LeadWrite) => Promise<void> }) {
  const { data } = useQuery(['growth', 'platforms', 'all'], (signal) => api.growth.platforms.list({}, signal))
  const p = lead.linked_platform
  const options = [{ value: '', label: 'Not linked' }, ...(data?.platforms ?? []).map((x) => ({ value: String(x.id), label: x.name }))]
  if (p && !data) options.push({ value: String(p.id), label: p.name })
  const auto = p ? BUCKET_LABELS[p.bucket] : lead.site_signals?.bucket ? BUCKET_LABELS[lead.site_signals.bucket] : BUCKET_LABELS[lead.bucket_set_by_hand ? 'unknown' : lead.bucket]
  const bucketOptions = [{ value: '', label: `Automatic: ${auto}` }, ...BUCKETS.map((b) => ({ value: b, label: BUCKET_LABELS[b] }))]
  return (
    <Card>
      <CardHeader title="What they use today" description="A bucket is a suggestion: any lead can be emailed." />
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <BucketBadge bucket={lead.bucket} />
          {p ? (
            <>
              <VsBadge value={p.vs_scorelm} />
              <PriceVsBadge value={p.price_vs_scorelm} />
              {p.verified ? null : <Badge tone="outline">Unverified</Badge>}
            </>
          ) : null}
        </div>
        {p ? (
          <div className="grid gap-1">
            <p className="text-[13.5px] text-ink">
              <Link to={`/growth/platforms/${p.id}`} className="font-semibold underline decoration-line-strong underline-offset-2 hover:text-accent">
                {p.name}
              </Link>
              <span className="text-muted"> · {platformPrice(p)}</span>
            </p>
            {p.vs_reason ? <p className="whitespace-pre-wrap break-words text-[13px] text-muted">{p.vs_reason}</p> : null}
          </div>
        ) : (
          <p className="text-[13px] text-muted">{lead.platform === 'unknown' ? 'No platform found.' : lead.platform_status === 'own_site' ? `${lead.platform}: a live site with no platform on it.` : `Written as ${lead.platform}. It isn't linked to a platform we know.`}</p>
        )}
        {lead.found_on_platform ? <p className="text-[13px] text-muted">Found on {lead.found_on_platform}.{lead.platform_abandoned ? ` ${lead.platform_abandoned.quote}.` : ''}</p> : null}
        <EditableSelectField label="Platform" value={lead.platform_id ? String(lead.platform_id) : ''} options={options} onSave={(v) => save({ platform_id: v ? Number(v) : null })} />
        {p ? null : <EditableTextField label="Platform as written" value={lead.platform} maxLength={64} validate={(v) => (v ? null : "Platform can't be empty.")} onSave={(platform) => save({ platform })} />}
        <EditableSelectField
          label="Bucket"
          value={lead.bucket_set_by_hand ? lead.bucket : ''}
          options={bucketOptions}
          hint={lead.bucket_set_by_hand ? 'Set by hand, so it stays until you choose Automatic.' : undefined}
          onSave={(v) => save({ bucket: (v || null) as Bucket | null })}
        />
        <div className="grid gap-1.5">
          <p className="text-xs font-semibold text-muted">What their site shows</p>
          <SiteSignalList site={lead.site_signals} />
        </div>
      </div>
    </Card>
  )
}

function VerifyCard({ lead, stopped }: { lead: Lead; stopped: boolean }) {
  const follow = useActivityRun('verify')
  const [problem, setProblem] = useState<string | null>(null)
  const [deciding, setDeciding] = useState(false)
  // Verify again on this page is a person asking for a fresh check: it replaces their call.
  const verify = async () => {
    setProblem(null)
    try {
      const run = await api.growth.leads.verify([lead.id], { replaceDecisions: true })
      follow.started(run.id)
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }
  const decide = async (status: VerifyDecisionStatus) => {
    setProblem(null)
    setDeciding(true)
    try {
      const next = await api.growth.leads.decideVerification(lead.id, status)
      setQueryData(['growth', 'lead', lead.id], next)
      invalidateQueries('growth')
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setDeciding(false)
    }
  }
  return (
    <Card>
      <CardHeader
        title="How we checked this"
        description="Where the league lives today, read from its live site, with the proof."
        actions={
          stopped ? undefined : (
            <Button size="sm" disabled={follow.busy} onClick={() => void verify()}>
              {lead.verified_at ? 'Verify again' : 'Verify'}
            </Button>
          )
        }
      />
      <div className="grid gap-3">
        <ActivityRunBanner follow={follow} words={VERIFY_RUN_WORDS} />
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
        {lead.verify_decision ? <DecisionNote decision={lead.verify_decision} /> : null}
        {stopped ? null : (
          <div role="group" aria-label="Your call" className="flex flex-wrap items-center gap-2">
            {lead.verify_status !== 'verified' ? (
              <Button size="sm" disabled={deciding} onClick={() => void decide('verified')}>
                Mark verified
              </Button>
            ) : null}
            {lead.verify_status !== 'failed' ? (
              <Button size="sm" disabled={deciding} onClick={() => void decide('failed')}>
                Mark failed
              </Button>
            ) : null}
            {lead.verify_decision ? (
              <Button size="sm" variant="ghost" disabled={deciding} onClick={() => void decide('needs_review')}>
                Back to review
              </Button>
            ) : null}
          </div>
        )}
        <VerifyDetails facts={lead} />
      </div>
    </Card>
  )
}

function ActivityCard({ lead, stopped }: { lead: Lead; stopped: boolean }) {
  const follow = useActivityRun()
  const [problem, setProblem] = useState<string | null>(null)
  const check = async () => {
    setProblem(null)
    try {
      const run = await api.growth.leads.checkActivity([lead.id])
      follow.started(run.id)
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }
  return (
    <Card>
      <CardHeader
        title="Activity"
        description="Is the league still running? Only dated games, seasons and registrations count."
        actions={
          stopped ? undefined : (
            <Button size="sm" disabled={follow.busy} onClick={() => void check()}>
              Check activity
            </Button>
          )
        }
      />
      <div className="grid gap-3">
        <ActivityRunBanner follow={follow} />
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
        <ActivityDetails facts={lead} />
      </div>
    </Card>
  )
}

/**
 * The site checks (verification and activity), folded away by default (owner,
 * 2026-11-03: verified / unverified confused more than it helped). The checks
 * and their Check again buttons are inside.
 */
function Research({ lead, stopped }: { lead: Lead; stopped: boolean }) {
  // Open when Leads › For review sent them here (#checks): they came to decide.
  const { hash } = useLocation()
  const [open, setOpen] = useState(hash === '#checks')
  return (
    <section id="checks" aria-label="How we checked this lead" className="grid gap-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-2 rounded-lg border border-line bg-panel px-4 py-3 text-left text-[13px] font-semibold text-ink transition-colors hover:border-line-strong"
      >
        <span>How we checked this lead</span>
        <span className="text-xs font-medium text-muted">{VERIFY_LABELS[lead.verify_status]} · {open ? 'Hide' : 'Show'}</span>
      </button>
      {open ? (
        <>
          <VerifyCard lead={lead} stopped={stopped} />
          <ActivityCard lead={lead} stopped={stopped} />
        </>
      ) : null}
    </section>
  )
}

export default function LeadDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const navigate = useNavigate()
  const toast = useToast()
  const [dialog, setDialog] = useState<'delete' | 'replied' | null>(null)

  const { data: lead, error, status, refetch } = useQuery(validId ? ['growth', 'lead', id] : null, (signal) => api.growth.leads.get(id, signal))
  useDocumentTitle(lead?.name ?? 'Lead')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState
          title="Lead not found"
          description={validId ? `There's no lead with id ${id}.` : `“${rawId}” isn't a lead id.`}
          action={<ButtonLink to="/growth/leads">Back to leads</ButtonLink>}
        />
      </Card>
    )
  }
  if (status === 'loading' && !lead) return <LoadingState label="Loading lead…" />
  if (!lead) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this lead" onRetry={refetch} />
      </Card>
    )
  }

  const stopped = lead.status === 'do_not_contact'
  const canMarkReplied = !stopped && lead.status !== 'replied'
  const save = async (patch: LeadWrite) => {
    const next: Lead = await api.growth.leads.update(lead.id, patch)
    setQueryData(['growth', 'lead', id], next)
    invalidateQueries('growth')
  }
  const text = (key: keyof LeadWrite) => (v: string) => save({ [key]: v || null })
  const required = (label: string) => (v: string) => (v ? null : `${label} can't be empty.`)

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Leads', to: '/growth/leads' }, { label: lead.name }]}
        title={lead.name}
        subtitle={lead.email ? <span className="break-all">{lead.email}</span> : undefined}
        badges={<LeadStatusBadge status={lead.status} />}
        actions={
          stopped ? (
            <>
              <LeadPreviewAction lead={lead} />
              {lead.signup_id ? <ButtonLink to={`/tenants/signups/${lead.signup_id}`}>View tenant</ButtonLink> : null}
            </>
          ) : (
            <>
              <LeadPreviewAction lead={lead} />
              <LeadEmailButton lead={lead} />
              {lead.signup_id ? <ButtonLink to={`/tenants/signups/${lead.signup_id}`}>View tenant</ButtonLink> : null}
              {canMarkReplied ? (
                <Button onClick={() => setDialog('replied')}>Mark as replied…</Button>
              ) : null}
              <Button variant="ghost" icon={<TrashIcon size={15} />} onClick={() => setDialog('delete')}>
                Delete…
              </Button>
            </>
          )
        }
      />

      {stopped ? (
        <InlineAlert tone="bad" title="Do not contact">
          They asked us to stop. This lead stays on the list: its status can't change, it can't be deleted, and no import can bring its address back.
        </InlineAlert>
      ) : null}

      {/* Owner, 2026-11-03: read the lead, build its preview, email them. The
          details and the timeline on the left; what to do next on the right. */}
      <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="grid min-w-0 content-start gap-4">
          <Card>
            <CardHeader title="Details" />
            <div className="grid gap-4 sm:grid-cols-2">
              <EditableTextField className="sm:col-span-2" label="Name" value={lead.name} maxLength={255} validate={required('Name')} onSave={(name) => save({ name })} />
              <EditableSelectField label="Type" value={lead.type} options={TYPE_OPTIONS} onSave={(v) => save({ type: v as Lead['type'] })} />
              <EditableTextField label="Sport" value={lead.sport ?? ''} maxLength={64} onSave={text('sport')} />
              <EditableTextField label="City" value={lead.city ?? ''} maxLength={120} onSave={text('city')} />
              <EditableTextField label="State" value={lead.state ?? ''} maxLength={64} onSave={text('state')} />
              <EditableTextField label="Country" value={lead.country} maxLength={64} validate={required('Country')} onSave={(country) => save({ country })} />
              <EditableTextField className="sm:col-span-2" label="Website" value={lead.website ?? ''} mono hint={lead.website ? <SafeExternalLink href={lead.website} /> : undefined} onSave={text('website')} />
            </div>
            <div className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
              <p className="text-2xs font-bold uppercase tracking-eyebrow text-muted sm:col-span-2">Public contact</p>
              <EditableTextField className="sm:col-span-2" label="Contact name" value={lead.contact_name ?? ''} maxLength={255} onSave={text('contact_name')} />
              <EditableTextField label="Email" value={lead.email ?? ''} mono maxLength={255} onSave={text('email')} />
              <EditableTextField label="Phone" value={lead.phone ?? ''} maxLength={64} onSave={text('phone')} />
              <p className="text-xs text-subtle sm:col-span-2">Public organisation contacts only. Nothing about players or minors.</p>
            </div>
          </Card>

          <LeadConversation lead={lead} canEmail={!stopped && !lead.suppressed} />

          <Card>
            <CardHeader title="Notes" />
            <EditableTextField label="Notes" value={lead.notes ?? ''} multiline onSave={text('notes')} hint="Saves when you leave the box." />
          </Card>

          <Timeline leadId={lead.id} />
        </div>

        <div className="grid min-w-0 content-start gap-4">
          <LeadPreviewCard lead={lead} />
          <Card>
            <CardHeader title="Pipeline" />
            <div className="grid gap-4">
              {stopped ? null : <EditableSelectField label="Status" value={lead.status} options={STATUS_OPTIONS} onSave={(v) => save({ status: v as LeadStatus })} />}
              <EditableTextField
                label="Priority"
                value={String(lead.priority)}
                maxLength={3}
                hint="0 to 100, set by hand."
                validate={(v) => (/^\d+$/.test(v) && Number(v) <= 100 ? null : 'Use a whole number from 0 to 100.')}
                onSave={(v) => save({ priority: Number(v) })}
              />
              <EditableTextField
                label="Last contacted"
                value={lead.last_contacted_at?.slice(0, 10) ?? ''}
                placeholder="YYYY-MM-DD"
                validate={(v) => (!v || /^\d{4}-\d{2}-\d{2}$/.test(v) ? null : 'Use YYYY-MM-DD.')}
                onSave={(v) => save({ last_contacted_at: v || null })}
              />
              <EditableTextField
                label="League id"
                value={lead.linked_league_id ? String(lead.linked_league_id) : ''}
                hint={lead.linked_league ? <Link to={`/tenants/${lead.linked_league.id}/overview`} className="underline underline-offset-2 hover:text-accent">{lead.linked_league.name}</Link> : 'The league, once this lead becomes one.'}
                validate={(v) => (!v || /^\d+$/.test(v) ? null : 'Use the number on the tenant page.')}
                onSave={(v) => save({ linked_league_id: v ? Number(v) : null })}
              />
              <EditableTextField label="Source" value={lead.source} maxLength={64} validate={required('Source')} onSave={(source) => save({ source })} />
              <EditableTextField label="Source URL" value={lead.source_url ?? ''} mono hint={lead.source_url ? <SafeExternalLink href={lead.source_url} /> : 'Where this lead was found.'} onSave={text('source_url')} />
              <KeyValueList items={[{ label: 'Added', value: formatDate(lead.created_at) }]} />
            </div>
          </Card>
          <PlatformCard lead={lead} save={save} />
          <Research lead={lead} stopped={stopped} />
        </div>
      </div>

      <ConfirmDialog
        open={dialog === 'replied'}
        onClose={() => setDialog(null)}
        title={`Mark ${lead.name} as replied?`}
        description="Replies are normally spotted in the inbox. Use this when one was missed: any follow-up still waiting for them stops."
        confirmLabel="Mark as replied"
        tone="primary"
        onConfirm={async () => {
          await api.growth.leads.markReplied(lead.id)
          invalidateQueries('growth')
        }}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onClose={() => setDialog(null)}
        title={`Delete ${lead.name}?`}
        description="This removes the lead and its timeline."
        confirmLabel="Delete lead"
        onConfirm={async () => {
          await api.growth.leads.delete(lead.id)
          invalidateQueries('growth')
          toast.success(`${lead.name} is deleted.`)
          navigate('/growth/leads')
        }}
      />
    </div>
  )
}
